import type {
  ToastType,
  HierarchicalItem as DSHierarchicalItem,
} from "@legal-500/legal500-design-system";
import {
  Breadcrumb,
  Button,
  Icon,
  SearchBar,
  Toast,
  TreeNode,
} from "@legal-500/legal500-design-system";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "../../hooks/useApi";
import type { HierarchicalItem, HierarchyManagerConfig } from "../../types/uiTypes";

// Type bridge: Design system TreeNode uses number IDs, our app uses string IDs
// We cast at the boundary between our app and the design system
import { FormSuccessScreen, HierarchyForm } from "../Form";
import { DeleteConfirmationModal } from "../Modal/DeleteConfirmationModal";
import { DragConfirmationModal } from "../Modal/DragConfirmationModal";
import { DragErrorModal } from "../Modal/DragErrorModal";
import { UnsavedChangesModal } from "../Modal/UnsavedChangesModal";
import { UnauthorizedView } from "../UnauthorizedView";
import EmptyState from "../EmptyState";
import styles from "./HierarchyManager.module.css";
import { useBreadcrumbs } from "./hooks/useBreadcrumbs";
import { useDragAndDrop } from "./hooks/useDragAndDrop";
import { useModalState } from "./hooks/useModalState";
import { useTreeData } from "./hooks/useTreeData";
import { useUrlSync } from "./hooks/useUrlSync";
import PermissionGate from "../Permissions/PermissionGate";
import PermissionBanner from "../Permissions/PermissionBanner";
import { usePermissions } from "../../hooks/usePermissions";
import LoadingState from "../LoadingState";

// No-op function for disabled actions
const noop = () => {
  // Intentionally empty
};

export function HierarchyManager<T extends HierarchicalItem>({
  config,
}: {
  config: HierarchyManagerConfig<T>;
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState("");
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [selected, setSelected] = useState<string | null>(null);
  const [highlighted, setHighlighted] = useState<Set<string>>(new Set());
  const [sidebarWidth, setSidebarWidth] = useState(500);
  const [isResizing, setIsResizing] = useState(false);
  const [toast, setToast] = useState<{
    message: string;
    type: ToastType;
  } | null>(null);
  const [showSuccessScreen, setShowSuccessScreen] = useState(false);
  const [successMode, setSuccessMode] = useState<"create" | "edit" | "delete">("create");
  const [successItemName, setSuccessItemName] = useState<string>();
  const highlightTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const { hasPermission } = usePermissions();
  // In read-only mode we force-disable all mutation affordances regardless of
  // the user's taxonomy permissions, so Add/Edit/Delete buttons, drag-and-drop
  // and the create/edit form panel are suppressed.
  const canWrite = config.readOnly ? false : hasPermission("taxonomy.write");
  const canDelete = config.readOnly ? false : hasPermission("taxonomy.delete");

  // Handle resizing
  const handleMouseDown = useCallback(() => {
    setIsResizing(true);
  }, []);

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isResizing || !containerRef.current) return;

      const container = containerRef.current;
      const containerRect = container.getBoundingClientRect();
      const newWidth = e.clientX - containerRect.left;

      // Min width 300px, max width 80% of container
      const minWidth = 300;
      const maxWidth = containerRect.width * 0.8;

      if (newWidth >= minWidth && newWidth <= maxWidth) {
        setSidebarWidth(newWidth);
      }
    },
    [isResizing],
  );

  const handleMouseUp = useCallback(() => {
    setIsResizing(false);
  }, []);

  useEffect(() => {
    if (isResizing) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
    } else {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
  }, [isResizing, handleMouseMove, handleMouseUp]);

  const { data, loading, error, refetch } = useQuery(() => config.apiService.getAll());

  // Use custom hooks
  const { treeData, findItemById, filterTree } = useTreeData<T>(data ?? undefined);

  const getParentItem = useCallback(
    (item: T): T | undefined => {
      return item.parentId ? findItemById(treeData, item.parentId) : undefined;
    },
    [treeData, findItemById],
  );

  const {
    modal,
    setModal,
    deleteModal,
    setDeleteModal,
    setHasUnsavedChanges,
    pendingNavigation,
    setPendingNavigation,
    withUnsavedChangesGuard,
    openCreateModal,
    openEditModal,
    closeModal,
  } = useModalState<T>({ getParentItem });

  const {
    draggedItem,
    dragOverItem,
    dragModal,
    dragErrorModal,
    handleDragStart: onDragStart,
    handleDragOver: onDragOver,
    handleDrop: onDrop,
    handleConfirmDrag,
    handleCloseDragModal,
    handleCloseDragErrorModal,
  } = useDragAndDrop<T>({
    enabled: config.enableDragAndDrop !== false,
    apiService: config.apiService,
    singularName: config.singularName,
    onSuccess: message => setToast({ message, type: "success" }),
    onError: message => setToast({ message, type: "error" }),
    refetch,
  });

  const breadcrumbs = useBreadcrumbs<T>(treeData, selected, data ?? undefined);

  useUrlSync<T>({
    searchParams,
    modal,
    data: data ?? undefined,
    findItemById,
    treeData,
    // In read-only mode there is no edit form, so never let a `?edit=` URL param
    // open the form panel.
    openEditModal: config.readOnly ? noop : openEditModal,
    setModal,
    setSearchParams,
  });

  const filteredData = config.enableSearch !== false ? filterTree(treeData, searchTerm) : treeData;

  // Expand all nodes when searching
  useEffect(() => {
    if (searchTerm && data && config.enableSearch !== false) {
      const allIds = new Set(data.map(item => item.id));
      setExpanded(allIds);
    }
  }, [searchTerm, data, config.enableSearch]);

  const handleToggle = (id: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const allExpanded = data && expanded.size === data.length;

  const handleToggleAll = () => {
    if (allExpanded) {
      setExpanded(new Set());
    } else if (data) {
      const allIds = new Set(data.map(item => item.id));
      setExpanded(allIds);
    }
  };

  const handleEditItem = useCallback(
    (item: T) => {
      withUnsavedChangesGuard(() => {
        setSelected(item.id);
        setShowSuccessScreen(false);
        openEditModal(item);
      });
    },
    [withUnsavedChangesGuard, openEditModal],
  );

  const handleConfirmDelete = async (item: T) => {
    await config.apiService.delete(item.id);
    setDeleteModal(null);
    setSelected(null);

    // Show success screen
    setSuccessMode("delete");
    setSuccessItemName(item.name);
    setShowSuccessScreen(true);

    refetch();
  };

  const setHighlightWithTimeout = (itemId: string) => {
    if (highlightTimeoutRef.current) {
      clearTimeout(highlightTimeoutRef.current);
    }

    setHighlighted(new Set([itemId]));

    highlightTimeoutRef.current = setTimeout(() => {
      setHighlighted(new Set());
      highlightTimeoutRef.current = null;
    }, 3000);
  };

  useEffect(() => {
    return () => {
      if (highlightTimeoutRef.current) {
        clearTimeout(highlightTimeoutRef.current);
      }
    };
  }, []);

  const handleSaveItem = async (formData: Record<string, unknown>): Promise<void> => {
    const isCreating = modal?.type === "create";

    if (isCreating) {
      const newItem = await config.apiService.create(formData as Partial<T>);

      if (newItem?.id) {
        setHighlightWithTimeout(newItem.id);
      }
    } else if (modal?.type === "edit" && modal.item) {
      const newParentId = formData.parentId;
      const parentChanged = modal.item.parentId !== newParentId;

      await config.apiService.update(modal.item.id, formData as Partial<T>);

      if (parentChanged && config.apiService.move) {
        await config.apiService.move(modal.item.id, newParentId ? String(newParentId) : null);
      }

      setHighlightWithTimeout(modal.item.id);
    }

    // Reset unsaved changes state since form was successfully saved
    setHasUnsavedChanges(false);

    // Show success screen
    setSuccessMode(isCreating ? "create" : "edit");
    setSuccessItemName(formData.name as string | undefined);
    setShowSuccessScreen(true);

    // Refetch data
    refetch();
  };

  if (loading) {
    return <LoadingState message={`Loading ${config.title}...`} />;
  }

  if (error) {
    // Check if this is a 401 Unauthorized or 403 Forbidden error
    const status = (error as Error & { status?: number }).status;
    const isAuthError = status === 401 || status === 403;

    if (isAuthError) {
      return <UnauthorizedView />;
    }
  }

  // Show error state if data fetch failed
  if (error && !loading) {
    return (
      <div className={styles.wrapper}>
        <div className={styles.header}>
          <h1 className={styles.title}>{config.title}</h1>
        </div>
        <EmptyState
          variant="error"
          title={`Failed to load ${config.title.toLowerCase()}`}
          message={error.message}
          action={{
            label: "Retry",
            onClick: () => refetch(),
          }}
        />
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <h1 className={styles.title}>{config.title}</h1>
        {!config.readOnly && (
          <PermissionGate permission="taxonomy.write">
            <Button
              onClick={() => {
                setShowSuccessScreen(false);
                openCreateModal();
              }}
              variant="contained"
              color="primary"
              startIcon={<Icon icon="plus" size="small" />}
              className={styles.addButton}
              aria-label={`Add ${config.singularName}`}
            >
              <span className={styles.addButtonText}>Create</span>
            </Button>
          </PermissionGate>
        )}
      </div>
      <PermissionBanner />

      <div className={styles.mainContent} ref={containerRef}>
        <div className={styles.sidebar} style={{ width: `${sidebarWidth}px` }}>
          <div className={styles.treeContainer} role="tree" aria-label={config.title}>
            <div className={styles.treeToolbar}>
              {config.enableSearch !== false && (
                <SearchBar
                  value={searchTerm}
                  onChange={setSearchTerm}
                  onClear={() => setSearchTerm("")}
                  placeholder={`Search ${config.pluralName}...`}
                  className={styles.searchBar}
                />
              )}
              {config.treeMode !== "list" && filteredData.length > 0 && (
                <button
                  onClick={handleToggleAll}
                  className={styles.toggleAllButton}
                  aria-label={allExpanded ? "Collapse all" : "Expand all"}
                  title={allExpanded ? "Collapse all" : "Expand all"}
                >
                  <Icon icon={allExpanded ? "chevron-up" : "chevron-down"} size="small" />
                </button>
              )}
            </div>
            <div className={styles.treeList}>
              {filteredData.length > 0 ? (
                filteredData.map(item => (
                  <TreeNode
                    key={item.id}
                    item={item as unknown as DSHierarchicalItem}
                    level={0}
                    expanded={expanded as unknown as Set<number>}
                    selected={selected as unknown as number | null}
                    highlighted={highlighted as unknown as Set<number>}
                    draggedItem={draggedItem as unknown as DSHierarchicalItem | null}
                    dragOverItem={dragOverItem as unknown as DSHierarchicalItem | null}
                    searchTerm={searchTerm}
                    mode={config.treeMode}
                    enableDragDrop={config.enableDragAndDrop !== false && canWrite}
                    onToggle={handleToggle as unknown as (id: number) => void}
                    onSelect={
                      config.readOnly
                        ? noop
                        : (handleEditItem as unknown as (item: DSHierarchicalItem) => void)
                    }
                    onEdit={
                      canWrite
                        ? (handleEditItem as unknown as (item: DSHierarchicalItem) => void)
                        : noop
                    }
                    onAddChild={
                      canWrite
                        ? (parentItem: DSHierarchicalItem) => {
                            setShowSuccessScreen(false);
                            openCreateModal(parentItem as unknown as T);
                          }
                        : noop
                    }
                    onDragStart={onDragStart as unknown as (item: DSHierarchicalItem) => void}
                    onDragOver={
                      onDragOver as unknown as (
                        e: React.DragEvent,
                        item: DSHierarchicalItem,
                      ) => void
                    }
                    onDrop={
                      onDrop as unknown as (e: React.DragEvent, item: DSHierarchicalItem) => void
                    }
                    showCode={config.showCode}
                    showSlug={config.showSlug}
                    customIcon={
                      config.customNodeIcon as
                        | ((item: DSHierarchicalItem) => React.ReactNode)
                        | undefined
                    }
                    customLabel={
                      config.customNodeLabel as
                        | ((item: DSHierarchicalItem) => React.ReactNode)
                        | undefined
                    }
                  />
                ))
              ) : (
                <div className={styles.noData}>
                  <Icon icon="info" size="xlarge" className={styles.noDataIcon} />
                  <p className={styles.noDataText}>
                    {searchTerm
                      ? `No ${config.pluralName} found matching your search`
                      : canWrite
                        ? `No ${config.pluralName} found. Click 'Add ${config.singularName}' to create one.`
                        : `No ${config.pluralName} found.`}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
        <div
          className={`${styles.resizer} ${isResizing ? styles.resizerActive : ""}`}
          onMouseDown={handleMouseDown}
        >
          <div className={styles.resizerHandle}>
            <Icon icon="grip-vertical" size="small" />
          </div>
        </div>
        {modal ? (
          <div className={styles.contentPanel}>
            <div className={styles.formPanelHeader}>
              <div>
                {modal.type === "edit" &&
                  config.enableBreadcrumbs !== false &&
                  breadcrumbs.length > 0 && (
                    <div className={styles.formBreadcrumbs}>
                      <Icon icon="chevron-right" size="small" className={styles.breadcrumbIcon} />
                      <Breadcrumb
                        items={breadcrumbs}
                        separator={<Icon icon="chevron-right" size="small" />}
                        onItemClick={crumb => {
                          const item = findItemById(treeData, String(crumb.id));
                          if (item) handleEditItem(item);
                        }}
                      />
                    </div>
                  )}
                <h2 className={styles.formPanelTitle}>
                  {modal.type === "create"
                    ? `Create ${config.singularName}`
                    : canWrite
                      ? `Edit ${config.singularName}`
                      : modal.item?.name || config.singularName}
                </h2>
              </div>
              <button
                className={styles.formPanelClose}
                onClick={closeModal}
                aria-label="Close form"
              >
                <Icon icon="cross" size="small" />
              </button>
            </div>
            <div className={styles.formPanelContent}>
              {showSuccessScreen ? (
                <FormSuccessScreen
                  mode={successMode}
                  itemName={successItemName}
                  singularName={config.singularName}
                  onClose={() => {
                    setShowSuccessScreen(false);
                    closeModal();
                  }}
                  onCreateAnother={
                    successMode === "create"
                      ? () => {
                          setShowSuccessScreen(false);
                          openCreateModal(modal?.parentItem);
                        }
                      : undefined
                  }
                />
              ) : (
                <HierarchyForm
                  key={`${modal.type}-${modal.item?.id || "new"}`}
                  mode={modal.type}
                  item={modal.item}
                  parentItem={modal.parentItem}
                  allItems={treeData}
                  fields={config.formFields}
                  validationSchema={config.validationSchema}
                  onClose={closeModal}
                  onSave={handleSaveItem}
                  onDelete={
                    modal.type === "edit" && modal.item && canDelete
                      ? () => setDeleteModal(modal.item!)
                      : undefined
                  }
                  onNavigateToChild={childId => {
                    const child = findItemById(treeData, childId);
                    if (child) handleEditItem(child);
                  }}
                  onDirtyChange={setHasUnsavedChanges}
                  singularName={config.singularName}
                  readOnly={!canWrite}
                  formHeader={config.formHeader?.(modal)}
                  formFooter={config.formFooter}
                  parentOptionLabel={config.parentOptionLabel}
                />
              )}
            </div>
          </div>
        ) : filteredData.length > 0 ? (
          <div className={styles.emptyState}>
            <Icon icon="info" size="xlarge" className={styles.emptyStateIcon} />
            <p className={styles.emptyStateText}>Click on a {config.singularName} to edit</p>
          </div>
        ) : null}
      </div>
      {deleteModal && (
        <DeleteConfirmationModal
          item={deleteModal}
          singularName={config.singularName}
          onClose={() => setDeleteModal(null)}
          onConfirm={handleConfirmDelete}
        />
      )}
      {dragModal && (
        <DragConfirmationModal
          draggedItem={dragModal.draggedItem}
          targetItem={dragModal.targetItem}
          singularName={config.singularName}
          onClose={handleCloseDragModal}
          onConfirm={handleConfirmDrag}
        />
      )}
      {dragErrorModal && (
        <DragErrorModal message={dragErrorModal} onClose={handleCloseDragErrorModal} />
      )}
      {pendingNavigation && (
        <UnsavedChangesModal
          onClose={() => setPendingNavigation(null)}
          onConfirm={() => {
            pendingNavigation.action();
            setPendingNavigation(null);
          }}
        />
      )}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
          duration={toast.type === "error" ? 5000 : 3000}
        />
      )}
    </div>
  );
}
